import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { checkBeketValidate } from 'widgets/beket/beket-search-form/lib/checkBeketValidate.ts'
import LevelCount, { Count } from 'widgets/level-count/level-count.tsx'

import { BeketFindTicketForm } from 'entities/beket/models/find-ticket/model'

import { dateFormat } from 'shared/lib/dateFormat'
import { IErrorForm, getFieldsError } from 'shared/lib/validate'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ButtonIconText } from 'shared/ui/button-icon-text'
import { Button } from 'shared/ui/button/button'
import { DepArrCountryBlock } from 'shared/ui/dep-arr-country-block'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import s from './styles.module.scss'

interface Props {
  isSearch?: boolean
  isAvailable?: boolean
  type?: 'white' | 'grey' | 'lightGrey'
  searchData: BeketFindTicketForm | Partial<BeketFindTicketForm>
  onChange?: (data?: Count) => void
  onSwitch?: () => void
  onSearch?: () => void
  onShowError?: () => void
}

export const BeketSearchForm = observer(
  ({
    isSearch,
    type,
    searchData,
    isAvailable,
    onChange,
    // onSwitch,
    onSearch,
    onShowError,
  }: Props) => {
    const navigate = useNavigate()

    const { closeDialog, openDialog } = useDialogBottomSheet()

    const { t } = useTranslation()

    const [errors, setErrors] = useState<IErrorForm | undefined>(undefined)

    const directionData = searchData?.directions?.[0]

    const onClickToSearch = (route: string) => {
      if (!isAvailable) {
        onShowError?.()
        return
      }

      if (route === 'from') return

      navigate({
        pathname: RoutingPaths.beket.searchDirection.path(route),
        search: isSearch ? `page=search` : '',
      })
    }

    const onClickDate = () => {
      if (!isAvailable) {
        onShowError?.()
        return
      }

      if (!directionData?.from?.id || !directionData?.to?.id) {
        setErrors({
          'directions[0].from.cityName': 'required',
          'directions[0].to.cityName': 'required',
        })
        return
      }

      navigate({
        pathname: RoutingPaths.beket.calendar.path(),
        search: isSearch ? `page=search` : '',
      })
    }

    // const onSwitchDirection = () => {
    //   onSwitch?.()
    // }

    const onSubmitCount = (data?: Count) => {
      onChange?.(data)

      closeDialog()
    }

    const onClickDetail = () => {
      openDialog({
        child: (
          <LevelCount
            title={t('passengers')}
            data={{
              adult: searchData.adult,
              child: searchData.child,
            }}
            onChangeCountHandler={onSubmitCount}
          />
        ),
        maxHeight: '42vh',
      })
    }

    const resultDate = dateFormat(directionData?.date || '')

    const validateForm = async () => {
      if (!isAvailable) {
        onShowError?.()
        return
      }

      try {
        await checkBeketValidate.validate(searchData, { abortEarly: false })

        setErrors(undefined)
        onSearch?.()
      } catch (error) {
        const resultValidate = getFieldsError(error)
        setErrors(resultValidate)
      }
    }

    const countPassengers = (searchData?.adult || 0) + (searchData?.child || 0)
    return (
      <div>
        <div className={s.block}>
          <DepArrCountryBlock
            type={type}
            from={{
              name: directionData?.from?.cityName,
            }}
            to={{
              name: directionData?.to?.cityName,
            }}
            onClick={onClickToSearch}
            // onSwitch={onSwitchDirection}
            error={
              !!errors?.['directions[0].from.cityName'] ||
              !!errors?.['directions[0].to.cityName']
            }
          />
        </div>

        <div className={clsx(s.dateWrapper, s.block)}>
          <div className={s.date}>
            <ButtonIconText
              height="100%"
              type={type}
              value={resultDate}
              placeholder={t('when')}
              onClick={onClickDate}
              error={!!errors?.['directions[0].date']}
            />
          </div>

          <div className={s.date}>
            <ButtonIconText
              type={type}
              height="100%"
              onClick={onClickDetail}
              placeholder={t('passengers')}
              rightIcon={AllIcons.PASSENGERS}
              value={t('beket.page.passenger_count', {
                count: countPassengers,
              })}
            />
          </div>
        </div>

        <Button
          className={s.button}
          onClick={validateForm}
          borderRadius={'md'}
        >
          <p className={s.text}>{t('beket_content.find_flight')}</p>
        </Button>
      </div>
    )
  },
)
